<template>
  <div>
    <b-card class="page-card">
      <h2 class="card-header">Deferred Work</h2>
      <responsive-search
        v-if="!isVcpUser"
        ref="search"
        :fields="searchFields"
        :is-busy="searchIsBusy"
        storage-key="deferredWorkSearch"
        @searchButtonClick="searchButtonClick"
        @resetButtonClick="resetButtonClick"
      />
      <b-row v-if="!$smallScreen" class="d-flex justify-content-between">
        <b-form-group>
          <b-input-group>
            <b-form-input
              id="filterInput"
              v-model="filter"
              variant="position-relative"
              type="search"
              placeholder="Type to Filter"
              :debounce="500"
            ></b-form-input>
          </b-input-group>
        </b-form-group>
        <div class="d-flex">
          <div class="mt-2 mr-3">{{ deferredJobsCount }} Deferred Jobs</div>
          <b-pagination
            v-model="currentPage"
            :total-rows="totalRows"
            :per-page="pageSize"
            aria-controls="my-table"
            align="right"
            class="ml-auto w-auto"
          ></b-pagination>
        </div>
      </b-row>
      <b-table
        striped
        class="border"
        no-border-collapse
        :sticky-header="$isCustomer ? '73vh' : '63vh'"
        :items="deferredJobs"
        :fields="fields"
        :per-page="pageSize"
        :current-page="currentPage"
        :filter="filter"
        :sort-by.sync="sortBy"
        show-empty
        empty-text="No jobs have been deferred for your branches."
        empty-filtered-text="No results found."
        @filtered="onFiltered"
      >
        <template #cell(vin)="{ item }">
          <safe-hyperlink :to="'/units/' + item.unitId">
            {{ item.vin }}
          </safe-hyperlink>
        </template>
        <template #cell(daysDeferred)="{ item }">
          {{ item.daysDeferred }}
        </template>
        <template #cell(jobId)="{ item }">
          {{ item.jobId }}
        </template>
        <template #cell(name)="{ item }">
          {{ item.name }}
        </template>
        <template #cell(jobSlsId)="{ item }">
          <div>
            <b-link v-if="isEstimateAvailable(item.jobSlsId)" :to="getEstimateLink(item.jobSlsId)" class="text">
              {{ item.jobSlsId }}
            </b-link>
            <b-link v-else-if="!$isCustomer" :to="`/units/${item.unitId}/service-orders/${item.jobSlsId}`">
              {{ item.jobSlsId }}
            </b-link>
            <div v-else>{{ item.jobSlsId }}</div>
          </div>
        </template>
        <template #cell(jobSubtotal)="{ item }">
          {{ item.jobSubtotal | currency }}
        </template>

        <template #cell(serviceStatus)="{ item, index }">
          <div v-if="item.serviceRequest && item.serviceRequest.appointment" :id="`startDate_${index}`">
            <b-badge
              v-if="$isCustomer"
              variant="light"
              :style="AppointmentCssHelper.badgeStyle(item.serviceRequest.appointment)"
            >
              {{ item.serviceRequest.appointment.appointmentStatus.name }}
            </b-badge>
            <b-badge
              v-else
              :href="`${shopSchedulerUrl}/schedule/${item.serviceRequest.appointment.branchId}`"
              variant="light"
              :style="AppointmentCssHelper.badgeStyle(item.serviceRequest.appointment)"
            >
              {{ item.serviceRequest.appointment.appointmentStatus.name }}
            </b-badge>
          </div>
          <!-- TODO make this a link to the VCP Customer Calendar -->
          <div v-else-if="item.serviceRequest" :id="`serviceDateRequested_${index}`">
            <b-badge variant="light" :style="AppointmentCssHelper.requestBadgeStyle()">Requested</b-badge>
          </div>
          <b-tooltip
            v-if="item.serviceRequest && item.serviceRequest.appointment"
            :target="`startDate_${index}`"
            boundary="window"
            triggers="hover"
          >
            {{ item.serviceRequest.appointment.startDate | dateTime }}
          </b-tooltip>
          <b-tooltip
            v-else-if="item.serviceRequest && item.serviceRequest.serviceDateRequested"
            :target="`serviceDateRequested_${index}`"
            boundary="window"
            triggers="hover"
          >
            {{ item.serviceRequest.serviceDateRequested | date }}
          </b-tooltip>
          <div v-if="item.openServiceOrderId && !$isCustomer" class="mt-1">
            Open SO ID:
            <b-link :to="'/units/' + item.unitId + '/service-orders/' + item.openServiceOrderId">
              {{ item.openServiceOrderId }}
            </b-link>
          </div>
        </template>

        <template #cell(actions)="{ item }">
          <div v-if="!$isCustomer || ($isCustomer && item.isAllowServiceRequest)" class="text-center">
            <b-dropdown size="xsm" right variant="secondary" no-caret boundary="viewport">
              <template #button-content>
                <font-awesome-icon icon="ellipsis-h" />
              </template>
              <b-link
                v-if="serviceRequestEnabled || vcpServiceRequestFlagEnabled"
                class="dropdown-item"
                @click="requestService(item)"
              >
                Schedule Service
              </b-link>
              <b-link
                v-if="!$isCustomer"
                :to="{
                  path: `/units/${item.unitId}/service-orders/create?mode=estimate`
                }"
                class="dropdown-item"
              >
                Create Estimate
              </b-link>
              <b-link
                v-if="!$isCustomer"
                :to="{
                  path: `/units/${item.unitId}/service-orders/create?mode=serviceOrder`
                }"
                class="dropdown-item"
              >
                Create Service Order
              </b-link>
              <b-link v-if="!$isCustomer" class="dropdown-item" @click="confirmDelete(item)">Remove job</b-link>
            </b-dropdown>
          </div>
        </template>
      </b-table>
    </b-card>
    <service-request-modal v-if="serviceRequestEnabled || vcpServiceRequestFlagEnabled" ref="serviceRequestModal" />
    <warning-modal
      id="DeferredJobDeleteWarning"
      ref="DeferredJobDeleteWarning"
      title="Warning"
      :warning-text="deleteText"
      continue-btn-text="Yes, Remove"
      cancel-btn-text="Cancel"
    ></warning-modal>
  </div>
</template>
<script>
// components
import ResponsiveSearchComponent from '@/shared/components/ResponsiveSearchComponent';
import SafeHyperlinkComponent from '@/shared/components/ui/SafeHyperlinkComponent';
import WarningModal from '@/shared/components/WarningModal';
// mixins
import SearchMixin from '@/shared/mixins/SearchMixin';
import AppointmentCssHelper from '@/shared/helpers/appointment-css-helper';

// vuex
import { RootGetters } from '@/shared/store/types';
import { DeferredJobActions, DeferredJobGetters } from '@/shared/store/deferred-job/types';
import { LookupGetters } from '@/shared/store/lookup/types';
import { mapActions, mapGetters, mapState } from 'vuex';
import { UserGetters } from '@/shared/store/user/types';
import { ServiceOrderActions } from '@/shared/store/service-order/types';
// helpers
import ErrorService from '@/shared/services/ErrorService';

export default {
  name: 'DeferredWork',
  components: {
    'warning-modal': WarningModal,
    'responsive-search': ResponsiveSearchComponent,
    'safe-hyperlink': SafeHyperlinkComponent,
    'service-request-modal': () => import('@/shared/components/ServiceRequestModal')
  },
  mixins: [SearchMixin],
  beforeRouteUpdate(to, from, next) {
    next();
    this.runSearch();
  },
  data: function () {
    return {
      pageSize: 50,
      currentPage: 1,
      totalRows: 0,
      filter: null,
      sortBy: null,
      searchIsBusy: false,
      deleteText: 'Are you sure you want to remove this job?',
      showList: [],
      AppointmentCssHelper: AppointmentCssHelper
    };
  },
  computed: {
    ...mapState('serviceOrder', ['deferredEstimateSearchResults']),
    ...mapGetters({
      offline: RootGetters.GET_OFFLINE,
      deferredJobsSearchResults: DeferredJobGetters.GET_DEFERRED_JOBS_SEARCH_RESULTS,
      isFeatureFlagEnabled: LookupGetters.IS_FEATURE_FLAG_ENABLED
    }),
    isVcpUser() {
      return this.$store.getters[UserGetters.AUTHORIZE_ROLE](['VcpUser']);
    },
    deferredJobs() {
      return this.deferredJobsSearchResults;
    },
    deferredJobsCount() {
      return this.deferredJobsSearchResults.length;
    },
    fields() {
      var fields = [
        { key: 'fleetUnitId', label: 'Fleet Unit Id', sortable: true },
        { key: 'vin', label: 'VIN/Serial', sortable: true },
        { key: 'customerId', label: 'Customer Id', sortable: true },
        { key: 'customerName', label: 'Customer Name', sortable: true },
        { key: 'branch', label: 'Branch', sortable: true },
        { key: 'daysDeferred', label: 'Days Deferred', sortable: true },
        { key: 'jobId', label: 'Job Id', sortable: true },
        { key: 'name', label: 'Job Name', sortable: true },
        { key: 'jobSubtotal', label: 'Job Subtotal', sortable: true },
        { key: 'jobSlsId', label: 'Estimate Id', sortable: true },
        { key: 'serviceStatus', label: 'Service Status', sortable: false }
      ];
      if (this.$isCustomer && this.vcpServiceRequestFlagEnabled) {
        fields.push({ key: 'actions', label: 'Actions', sortable: false });
      } else if (!this.$isCustomer) {
        fields.push({ key: 'actions', label: 'Actions', sortable: false });
      }
      return fields;
    },
    vcpFields() {
      return [
        { key: 'fleetUnitId', label: 'Fleet Unit Id', sortable: true },
        { key: 'vin', label: 'VIN/Serial', sortable: true },
        { key: 'customerId', label: 'Customer Id', sortable: true },
        { key: 'customerName', label: 'Customer Name', sortable: true },
        { key: 'branch', label: 'Branch', sortable: true },
        { key: 'daysDeferred', label: 'Days Deferred', sortable: true },
        { key: 'jobId', label: 'Job Id', sortable: true },
        { key: 'name', label: 'Job Name', sortable: true },
        { key: 'jobSubtotal', label: 'Job Subtotal', sortable: true },
        { key: 'jobSlsId', label: 'Estimate Id', sortable: true },
        { key: 'actions', label: 'Actions', sortable: false }
      ];
    },
    searchFields() {
      const customerField = this.$isCustomer ? this.myCustomersField : this.customerField;
      return [{ ...this.fleetUnitIdField }, { ...customerField }, { ...this.branchField }];
    },
    serviceRequestEnabled() {
      return this.isFeatureFlagEnabled('ServiceRequest');
    },
    vcpServiceRequestFlagEnabled() {
      return this.isFeatureFlagEnabled('VcpServiceRequest');
    }
  },
  mounted() {
    const query = this.$refs.search?.getQueryString();
    this.$router.replace({ query }).catch(() => {
      this.runSearch();
    });
  },
  async created() {
    await this[ServiceOrderActions.SEARCH_ESTIMATES]({ isDeferredHistorySearch: true });
  },
  methods: {
    ...mapActions([
      ServiceOrderActions.SEARCH_ESTIMATES,
      DeferredJobActions.SEARCH_DEFERRED_JOBS,
      DeferredJobActions.FETCH_DEFERRED_JOBS_COUNT,
      DeferredJobActions.DELETE_DEFERRED_JOB
    ]),
    onFiltered(filteredItems) {
      // Trigger pagination to update the number of buttons/pages due to filtering
      this.totalRows = filteredItems.length;
      this.currentPage = 1;
    },
    isEstimateAvailable(jobSlsId) {
      return this.deferredEstimateSearchResults.some(estimate => estimate.serviceOrderId === jobSlsId);
    },
    getEstimateLink(jobSlsId) {
      const estimate = this.deferredEstimateSearchResults.find(estimate => estimate.serviceOrderId === jobSlsId);
      if (estimate) {
        return `/units/${estimate.unitId}/service-orders/${estimate.serviceOrderId}`;
      }
    },
    async resetButtonClick() {
      this.filter = '';
      this.sortBy = '';
      this.searchButtonClick(null);
    },
    async searchButtonClick(query) {
      await this.$router.push({ query }).catch(() => {
        this.runSearch();
      });
    },
    async runSearch() {
      try {
        this.searchIsBusy = true;
        await this[DeferredJobActions.SEARCH_DEFERRED_JOBS](this.$route.query);
        this.totalRows = this.deferredJobs.length;
      } catch (error) {
        ErrorService.createErrorToast(this, 'Error searching Deferred Jobs.');
      } finally {
        this.searchIsBusy = false;
      }
    },
    confirmDelete: function (item) {
      this.$refs.DeferredJobDeleteWarning.show(this, this.deleteItem.bind(this, item));
    },
    requestService(item) {
      this.$refs['serviceRequestModal'].showAndLoad(item.unitId, item.jobId);
    },
    deleteItem: async function (item) {
      this.isLoading = true;
      let deletedJob = {
        jobId: item.jobId,
        name: item.name,
        complaint: item.complaint,
        cause: item.cause,
        correction: item.correction,
        deferredByEmployeeName: item.deferredByEmployeeName,
        deferredByEmployeeDate: item.deferredByEmployeeDate,
        jobSubtotal: item.jobSubtotal,
        jobSlsId: item.jobSlsId,
        jobOpsId: item.jobOpsId
      };
      await this[DeferredJobActions.DELETE_DEFERRED_JOB]({ item: deletedJob, unitId: item.unitId });
      await this.runSearch();
      await this[DeferredJobActions.FETCH_DEFERRED_JOBS_COUNT]();
      this.isLoading = false;
    }
  }
};
</script>
